import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(location.state?.from || "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your client portal & applications.">
      <form onSubmit={submit} data-testid="login-form" className="space-y-5">
        <AuthInput label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
        <AuthInput label="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
        {error && <p className="text-destructive text-sm" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-navy text-white font-medium hover:bg-cobalt transition-colors disabled:opacity-60">
          {loading ? "Signing in…" : "Sign in"} <ArrowRight size={18} />
        </button>
      </form>
      <p className="text-sm text-slateink mt-6">
        New here? <Link to="/register" data-testid="link-register" className="text-cobalt font-medium hover:underline">Create an account</Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen pt-[72px] bg-cream grid lg:grid-cols-2" data-testid="auth-shell">
      <div className="hidden lg:block relative bg-navy grain overflow-hidden">
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-9 h-9 bg-white text-navy grid place-items-center font-display text-xl">J</span>
            <span className="font-display text-xl font-bold text-white">Joy<span className="text-cobalt-light">.</span></span>
          </Link>
          <div>
            <h2 className="font-display text-4xl font-bold text-white leading-tight">Careers that<br /><span className="italic text-cobalt-light">command the room.</span></h2>
            <p className="text-white/50 mt-4 max-w-sm">Your resume, LinkedIn and next opportunity — all in one place.</p>
          </div>
          <span className="font-mono-ui text-xs text-white/30 uppercase tracking-widest">Joy Resume Builders & Consulting</span>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16 lg:py-0">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-4xl font-bold text-navy">{title}</h1>
          <p className="text-slateink mt-2 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function AuthInput({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="overline text-slateink block mb-2">{label}</span>
      <input required type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full border border-border px-4 py-3 outline-none focus:border-cobalt text-navy bg-white" />
    </label>
  );
}
